import classNames from 'classnames';
import {callOrReturn, InputRule, markInputRule, mergeAttributes} from '@tiptap/core';
import {Link} from '@tiptap/extension-link';
import type {LinkOptions} from '@tiptap/extension-link';

import {clearFormatStoredMarks} from '../utils';

import appStyles from '../app-post-style.module.css';

const protocols = new Set(['ftp', 'mailto', 'timeapp', 'http', 'https']);

function linkInputRule(config: Parameters<typeof markInputRule>[0]) {
    const defaultMarkInputRule = markInputRule(config);

    return new InputRule({
        find: config.find,
        handler(props) {
            const attributes = callOrReturn(config.getAttributes, undefined, props.match.slice());

            if (!attributes) {
                return null;
            }

            const protocol = attributes.href?.split(':')[0];

            if (protocol && !protocols.has(protocol.toLowerCase())) {
                return null;
            }

            defaultMarkInputRule.handler(props);
            clearFormatStoredMarks(props.state);
            props.state.tr.setMeta('preventAutolink', true);
        },
    });
}

/**
 * @see https://github.com/ueberdosis/tiptap/discussions/1865
 */
export const TimeWebkitLinkExtension = Link.extend<LinkOptions>({
    inclusive: false,
    renderHTML({HTMLAttributes}) {
        return [
            'a',
            mergeAttributes(this.options.HTMLAttributes, HTMLAttributes, {
                class: classNames(HTMLAttributes.class, appStyles.markdown__link),
                title: HTMLAttributes.title || HTMLAttributes.href,
            }),
            0,
        ];
    },
    addAttributes() {
        return {
            ...this.parent?.(),
            title: {
                default: null,
            },
        };
    },
    addInputRules() {
        return [
            linkInputRule({
                find: /(?:^|\s)\[([^\]]*)?]\((\S+)(?: ["“](.+)["”])?\)\s$/i,
                type: this.type,
                getAttributes(match) {
                    return {
                        title: match.pop()?.trim(),
                        href: match.pop()?.trim(),
                    };
                },
            }),
            linkInputRule({
                find: /(?:^|\s)<([a-zA-Z]+:\S+)>\s$/i,
                type: this.type,
                getAttributes(match) {
                    return {
                        href: match[match.length - 1]?.trim(),
                    };
                },
            }),
        ];
    },
}).configure({
    openOnClick: false,
    protocols: Array.from(protocols),
});

